/**
 * Signing out in one window signs out every window (TRUS-638).
 *
 * WHY
 * Every window of the app — the main window, a tabbed pop-out (/popout/app), a
 * page pop-out opened as its own browser tab (/popout/page), each iframe tab
 * inside those, and on desktop every app-* / messaging-* webview — mounts its
 * own AuthProvider. They share ONE sign-in (the token in localStorage), but
 * each keeps its own in-memory user and its own React Query cache. Sign-out
 * used to clear localStorage and the in-memory state of the window that did it,
 * and nothing else: the other windows kept rendering the member data they had
 * already loaded. Their polling could not notice either — a request that goes
 * out with no bearer token gets a 401 that apiClient deliberately ignores (that
 * is what stopped the old logout loop).
 *
 * HOW
 * The window that signs out (manual, idle timeout, a 401 the session cannot
 * recover from, a failed refresh) calls broadcastSignedOut(). It reaches the
 * other windows three ways, because no single transport reaches all of them:
 *   - a BroadcastChannel (`weavein-session`): every same-origin document in this
 *     browser profile, iframes included;
 *   - a short-lived localStorage marker: the `storage` event, for engines or
 *     webviews without BroadcastChannel;
 *   - on desktop, a Tauri event to every webview window.
 * Independently of the signal, a window also signs itself out when it sees
 * `auth_token` disappear (a `storage` event), or when it is about to send an
 * API request while it thinks it is signed in but the token is gone — so a
 * sign-out that never broadcast (an older build in another tab, a cleared
 * storage) is still caught.
 *
 * A window that RECEIVES the signal signs out locally only: it never
 * re-broadcasts (no ping-pong between windows) and never calls the backend
 * logout (the window that signed out has already blacklisted the token).
 * requestLocalSignOut is single-flight per session, so three transports and a
 * burst of requests sign a window out exactly once.
 *
 * No React here: AuthContext registers the local sign-out handler and tells
 * this module when a session is active; apiClient / authFetch ask
 * checkSessionTokenPresent() before each request.
 */

export const SESSION_CHANNEL = 'weavein-session';
export const SIGNED_OUT_TYPE = 'weavein:signed-out';
/** localStorage key of the fallback marker. Written then removed shortly after. */
export const SIGNED_OUT_STORAGE_KEY = 'weavein:signed-out';
/** Tauri event name (letters, digits, '-', '/', ':' and '_' only). */
export const SIGNED_OUT_DESKTOP_EVENT = 'weavein:signed-out';
/** How long the fallback marker stays in localStorage. */
export const MARKER_TTL_MS = 1000;

export type SignOutReason =
  | 'manual'
  | 'idle'
  | 'unauthorized'
  | 'refresh-failed'
  | 'all-devices'
  | 'token-removed'
  | 'missing-token'
  | (string & {});

export interface SignedOutMessage {
  type: typeof SIGNED_OUT_TYPE;
  reason: SignOutReason;
  /** ms epoch at the sender. */
  at: number;
  nonce: string;
}

export type LocalSignOutHandler = (reason: SignOutReason) => void;

// ---------------------------------------------------------------- state

let sessionActive = false;
/** When this window's current session became active (ms epoch). */
let sessionActiveSince = 0;
/** Single-flight latch: a local sign-out has been requested for this session. */
let localSignOutLatched = false;
let handler: LocalSignOutHandler | null = null;
/** Nonces already handled (or sent) by this document. */
const seenNonces = new Set<string>();

function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

function hasStoredToken(storage: Storage | null = safeStorage()): boolean {
  try {
    return !!storage?.getItem('auth_token');
  } catch {
    // Storage unreadable: do not sign anyone out on a guess.
    return true;
  }
}

function newNonce(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}

function isDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI__' in window;
}

/**
 * AuthContext calls this with true once a user is signed in in this window,
 * and with false as soon as it signs out (before it clears anything).
 */
export function setSessionActive(active: boolean, now: number = Date.now()): void {
  if (active && !sessionActive) {
    sessionActiveSince = now;
    localSignOutLatched = false;
  }
  sessionActive = active;
}

export function isSessionActive(): boolean {
  return sessionActive;
}

/** AuthContext's local sign-out. Returns an unregister function. */
export function registerLocalSignOutHandler(fn: LocalSignOutHandler): () => void {
  handler = fn;
  return () => {
    if (handler === fn) handler = null;
  };
}

/**
 * Sign THIS window out without telling anyone and without calling the backend.
 * Single-flight: only the first request per session runs the handler.
 * Returns true when it ran.
 */
export function requestLocalSignOut(reason: SignOutReason): boolean {
  if (!sessionActive || localSignOutLatched) return false;
  localSignOutLatched = true;
  sessionActive = false;
  try {
    handler?.(reason);
  } catch (err) {
    console.warn('[session] local sign-out handler failed', err);
  }
  return true;
}

// ---------------------------------------------------------------- send

/**
 * Tell every other window that this session has ended. Called once by the
 * window that signed out; receivers never call it.
 */
export function broadcastSignedOut(reason: SignOutReason, now: number = Date.now()): SignedOutMessage {
  const msg: SignedOutMessage = { type: SIGNED_OUT_TYPE, reason, at: now, nonce: newNonce() };
  seenNonces.add(msg.nonce);

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(SESSION_CHANNEL);
      ch.postMessage(msg);
      ch.close();
    }
  } catch {
    /* no BroadcastChannel: the storage marker below covers it */
  }

  const storage = safeStorage();
  try {
    storage?.setItem(SIGNED_OUT_STORAGE_KEY, JSON.stringify(msg));
    setTimeout(() => {
      try {
        // Only remove our own marker; a newer sign-out may have replaced it.
        if (storage?.getItem(SIGNED_OUT_STORAGE_KEY) === JSON.stringify(msg)) storage.removeItem(SIGNED_OUT_STORAGE_KEY);
      } catch {
        /* ignore */
      }
    }, MARKER_TTL_MS);
  } catch {
    /* storage unavailable */
  }

  if (isDesktop()) {
    import('@tauri-apps/api/event')
      .then(({ emit }) => emit(SIGNED_OUT_DESKTOP_EVENT, msg))
      .catch(() => {
        /* the other transports still reach same-origin webviews */
      });
  }
  return msg;
}

// ---------------------------------------------------------------- receive

export function parseSignedOutMessage(data: unknown): SignedOutMessage | null {
  let value = data;
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object') return null;
  const v = value as Partial<SignedOutMessage>;
  if (v.type !== SIGNED_OUT_TYPE || typeof v.nonce !== 'string' || typeof v.at !== 'number') return null;
  return { type: SIGNED_OUT_TYPE, reason: typeof v.reason === 'string' ? v.reason : 'unknown', at: v.at, nonce: v.nonce };
}

/** Handle a sign-out signal from another window. Returns true when it signed this window out. */
export function receiveSignedOut(data: unknown): boolean {
  const msg = parseSignedOutMessage(data);
  if (!msg || seenNonces.has(msg.nonce)) return false;
  seenNonces.add(msg.nonce);
  // A signal older than this window's session (delivered late, after the user
  // signed in again) is about a session that has already ended — unless the
  // token really is gone.
  if (msg.at < sessionActiveSince && hasStoredToken()) return false;
  return requestLocalSignOut(msg.reason);
}

/**
 * A `storage` event from another window. Signs this window out on the marker,
 * or when `auth_token` was removed (or storage cleared) while it is signed in.
 */
export function handleSessionStorageEvent(e: Pick<StorageEvent, 'key' | 'newValue' | 'oldValue'>): boolean {
  if (e.key === SIGNED_OUT_STORAGE_KEY) {
    return e.newValue ? receiveSignedOut(e.newValue) : false;
  }
  if ((e.key === 'auth_token' && e.newValue == null) || e.key === null) {
    if (hasStoredToken()) return false;
    return requestLocalSignOut('token-removed');
  }
  return false;
}

/**
 * Called before an API request leaves this window. While this window thinks
 * it is signed in, a missing token means another window signed out: sign out
 * here too (once) and tell the caller not to send the request. Returns true
 * when the request may go ahead.
 */
export function checkSessionTokenPresent(opts: { refreshing?: boolean } = {}): boolean {
  if (!sessionActive || opts.refreshing) return true;
  if (hasStoredToken()) return true;
  requestLocalSignOut('missing-token');
  return false;
}

/** Error an API client rejects with when the session ended in another window. */
export class SessionEndedError extends Error {
  readonly sessionEnded = true;
  constructor() {
    super('Signed out');
    this.name = 'SessionEndedError';
  }
}

/**
 * Listen for sign-out from other windows on every transport. Call once per
 * document (AuthProvider does). Returns a cleanup function.
 */
export function startSessionSignOutListener(win: Window = window): () => void {
  const cleanups: Array<() => void> = [];

  try {
    if (typeof BroadcastChannel !== 'undefined') {
      const ch = new BroadcastChannel(SESSION_CHANNEL);
      ch.onmessage = (e) => {
        receiveSignedOut(e.data);
      };
      cleanups.push(() => ch.close());
    }
  } catch {
    /* storage events still work */
  }

  const onStorage = (e: StorageEvent) => {
    handleSessionStorageEvent(e);
  };
  win.addEventListener('storage', onStorage);
  cleanups.push(() => win.removeEventListener('storage', onStorage));

  if (isDesktop()) {
    let disposed = false;
    let unlisten: (() => void) | null = null;
    import('@tauri-apps/api/event')
      .then(({ listen }) => listen(SIGNED_OUT_DESKTOP_EVENT, (event) => receiveSignedOut(event.payload)))
      .then((fn) => {
        if (disposed) fn();
        else unlisten = fn;
      })
      .catch(() => {
        /* not a Tauri webview after all */
      });
    cleanups.push(() => {
      disposed = true;
      unlisten?.();
    });
  }

  return () => cleanups.forEach((fn) => fn());
}

/** Tests only. */
export function __resetSessionBroadcastForTests(): void {
  sessionActive = false;
  sessionActiveSince = 0;
  localSignOutLatched = false;
  handler = null;
  seenNonces.clear();
}
